import React, { useState, useEffect } from 'react';
import { useToast, useLists } from '../../context';
import { useLang } from '../../i18n';
import { useListNames } from '../../hooks/useListNames';
import { keepInView } from '../shared/dom';
import { Modal } from '../shared/Modal';
import { normalizeName } from '../../logic/names';
import { createList, saveList } from '../../data/lists';
import { LIST_ICON_CHOICES, LIST_COLOR_CHOICES, LIST_NAME_MAX } from '../../config/lists';
import { useListActions } from './useListActions';
import { ListItems } from './ListItems';

const DEFAULT_ICON = '📋';   // a new list's icon until one is picked

/**
 * ListEditor — make a new Today list, or change one: name, icon, colour, what goes on
 * it (things to buy vs tasks), whether items take a quantity, its position on the
 * board, and delete (custom lists only). Built-in lists can be renamed / restyled /
 * moved, and reset back to default.
 *
 * Its "Items" section (ListItems) moves item names between lists — remembered (routes),
 * so they're suggested in their new list from then on and any on the open list move too —
 * or removes them from a list's suggestions.
 *
 * Props: list (omit to create), catalogs ({ listId: [{ name }] }), boardItems (open
 * list), allItems (loaded history — a list with none is deleted outright), author.
 */
export function ListEditor({ list, catalogs, boardItems, allItems, author, onClose }) {
  const { t } = useLang();
  const notify = useToast();
  const lists = useLists();
  const { name: listName } = useListNames();
  const actions = useListActions({ boardItems, allItems, author });
  const { failed } = actions;
  const creating = !list;
  const builtin = !!(list && list.builtin);

  const [label, setLabel] = useState(() => (list ? listName(list) : ''));
  const [icon, setIcon] = useState(() => (list ? list.icon : DEFAULT_ICON));
  const [color, setColor] = useState(() => (list ? list.color : nextFreeColor(lists.active)));
  const [kind, setKind] = useState(() => (list && !list.sourceable ? 'task' : 'buy'));
  const [qty, setQty] = useState(() => (list ? !list.noQty : true));
  const [err, setErr] = useState('');

  // A new list takes a quantity when it's a buy-list, unless the user chose otherwise.
  const [qtyTouched, setQtyTouched] = useState(false);
  useEffect(() => { if (creating && !qtyTouched) setQty(kind === 'buy'); }, [kind, creating, qtyTouched]);

  const clean = label.trim().replace(/\s+/g, ' ');
  const nameTaken = clean && lists.active.some(l => l.id !== (list && list.id)
    && normalizeName(listName(l)) === normalizeName(clean));

  const save = () => {
    if (!clean) { setErr(t('lists.nameRequired')); return; }
    if (nameTaken) { setErr(t('lists.nameTaken', { name: clean })); return; }
    if (creating) {
      const order = lists.active.reduce((m, l) => Math.max(m, l.order), 0) + 10;
      createList({ label: clean, icon, color, kind, qty, order, by: author }).catch(failed);
      notify(t('lists.created', { name: clean }));
      onClose();
      return;
    }
    const patch = {};
    // A built-in keeps its translated name unless it was actually renamed.
    if (clean !== listName(list)) patch.label = clean;
    if (icon !== list.icon) patch.icon = icon;
    if (color !== list.color) patch.color = color;
    if (!builtin) {
      if (kind !== list.kind) patch.kind = kind;
      if (qty !== !list.noQty) patch.qty = qty;
    }
    if (Object.keys(patch).length) {
      saveList(list.id, patch).catch(failed);
      notify(t('lists.saved'));
    }
    onClose();
  };

  // ── Position on the board / delete (custom) / reset (built-in) ───────────
  const idx = list ? lists.active.findIndex(l => l.id === list.id) : -1;
  const move = dir => actions.move(list, dir);
  const remove = () => { if (actions.remove(list)) onClose(); };
  const reset = () => { if (actions.reset(list)) onClose(); };

  return (
    <Modal backdropClassName="do-list-back" className="do-modal--wide do-list do-le" style={{ '--cc': color }} onClose={onClose}
      label={creating ? t('lists.createTitle') : t('lists.editTitle', { name: listName(list) })}>
      <div className="do-list__body">
        <h2 className="do-print__title">
          <span className="do-le__badge">{icon}</span>
          {creating ? t('lists.createTitle') : t('lists.editTitle', { name: listName(list) })}
        </h2>
        {builtin && <p className="do-list__sub do-le__note">{t('lists.builtinNote')}</p>}

        <label className="do-le__field">
          <span className="do-le__lbl">{t('lists.name')}</span>
          <input className="do-le__input" value={label} maxLength={LIST_NAME_MAX} autoFocus={creating}
            placeholder={t('lists.namePh')} onFocus={keepInView}
            onChange={e => { setLabel(e.target.value); setErr(''); }}
            onKeyDown={e => { if (e.key === 'Enter') save(); }} aria-invalid={!!err} />
        </label>
        {err && <p className="do-le__err" role="alert">{err}</p>}

        <div className="do-le__field">
          <span className="do-le__lbl">{t('lists.icon')}</span>
          <div className="do-le__icons" role="radiogroup" aria-label={t('lists.icon')}>
            {LIST_ICON_CHOICES.map(ic => (
              <button key={ic} type="button" role="radio" aria-checked={icon === ic}
                className={`do-le__icon${icon === ic ? ' do-le__icon--on' : ''}`} onClick={() => setIcon(ic)}>{ic}</button>
            ))}
          </div>
        </div>

        <div className="do-le__field">
          <span className="do-le__lbl">{t('lists.color')}</span>
          <div className="do-le__colors" role="radiogroup" aria-label={t('lists.color')}>
            {LIST_COLOR_CHOICES.map(c => (
              <button key={c} type="button" role="radio" aria-checked={color === c} aria-label={c}
                className={`do-le__color${color === c ? ' do-le__color--on' : ''}`} style={{ '--sw': c }} onClick={() => setColor(c)} />
            ))}
          </div>
        </div>

        {!builtin && (
          <div className="do-le__field">
            <span className="do-le__lbl">{t('lists.kind')}</span>
            <div className="do-le__kinds" role="radiogroup" aria-label={t('lists.kind')}>
              {[['buy', t('lists.kindBuy'), t('lists.kindBuySub')], ['task', t('lists.kindTask'), t('lists.kindTaskSub')]].map(([k, title, sub]) => (
                <button key={k} type="button" role="radio" aria-checked={kind === k}
                  className={`do-le__kind${kind === k ? ' do-le__kind--on' : ''}`} onClick={() => setKind(k)}>
                  <b>{title}</b><span>{sub}</span>
                </button>
              ))}
            </div>
            <label className="do-list__keep">
              <input type="checkbox" checked={qty} onChange={e => { setQty(e.target.checked); setQtyTouched(true); }} />
              {t('lists.qty')}
            </label>
          </div>
        )}

        {!creating && (
          <div className="do-le__field do-le__row">
            <span className="do-le__lbl">{t('lists.position', { n: idx + 1, total: lists.active.length })}</span>
            <button type="button" className="do-le__mini" onClick={() => move(-1)} disabled={idx <= 0}>{t('lists.moveEarlier')}</button>
            <button type="button" className="do-le__mini" onClick={() => move(1)} disabled={idx < 0 || idx >= lists.active.length - 1}>{t('lists.moveLater')}</button>
          </div>
        )}

        {!creating && (
          <ListItems list={list} catalogs={catalogs} boardItems={boardItems} />
        )}

        {!creating && !builtin && (
          <div className="do-le__danger">
            <button type="button" className="do-le__del" onClick={remove}>{t('lists.delete')}</button>
          </div>
        )}
        {builtin && list.restyled && (
          <div className="do-le__danger">
            <button type="button" className="do-le__mini" onClick={reset}>{t('lists.reset')}</button>
          </div>
        )}
      </div>
      <div className="do-print__actions">
        <button className="do-modal__btn" onClick={onClose}>{t('common.cancel')}</button>
        <button className="do-modal__btn do-modal__btn--primary" onClick={save} disabled={!clean}>
          {creating ? t('lists.create') : t('lists.save')}
        </button>
      </div>
    </Modal>
  );
}

/** First palette colour no list uses yet (else the first). */
function nextFreeColor(active) {
  const used = new Set(active.map(l => String(l.color).toLowerCase()));
  return LIST_COLOR_CHOICES.find(c => !used.has(c.toLowerCase())) || LIST_COLOR_CHOICES[0];
}
